import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from 'vue';

import { CollectionType } from '@/enums/collection-type';
import { ProviderFilterPlace } from '@/enums/provider-filter-place';

import { ModalHelper } from '@/helpers/modal-helper';
import { calculateUrl } from '@/helpers/state-helper';

import { NewPageType } from '@/@types/graphql-types';
import { FilterCollection } from '@/stores/filter.store';
import {
	GetUpcomingProvidersDocument,
	GetUpcomingProvidersQueryVariables,
	GetUpcomingProvidersQuery,
} from '@/pages/graphql/queries/GetUpcomingProviders.query';

import {
	useConstantStore,
	useFilterStore,
	useLanguageStore,
	useRoutingStore,
	useUserStore,
} from '@/helpers/composables/useStores';
import { useQuery } from '@/helpers/composables/useApollo';

import { PackagesSelectionModal } from '@/lazy-load-components';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { useRouter } from 'vue-router/composables';
import { WebLocale } from '@/enums/web-locale';
import type { Package } from '@/@types/graphql-types';

interface FilterBarProviderSeoProps {
	collectionType: CollectionType;
	currentFilters: FilterCollection;
	webLocale: WebLocale;
	addButtonVisible?: boolean;
	showAddButtonFirst?: boolean;
	userProviders: Package[];
	newPageType?: NewPageType;
	providersClickable?: boolean;
	updateRouteState?: boolean;
	trackEvent?: boolean;
	userSelectedPackages?: string[];
	selectionType?: 'single' | 'bundle';
}


export default /*#__PURE__*/_defineComponent({
  __name: 'FilterBarProviderSeo',
  props: {
    collectionType: null,
    currentFilters: null,
    webLocale: null,
    addButtonVisible: { type: Boolean, default: true },
    showAddButtonFirst: { type: Boolean, default: false },
    userProviders: null,
    newPageType: null,
    providersClickable: { type: Boolean, default: true },
    updateRouteState: { type: Boolean, default: true },
    trackEvent: { type: Boolean, default: true },
    userSelectedPackages: null,
    selectionType: { default: 'single' }
  },
  emits: ["toggle"],
  setup(__props: any, { emit }: { emit: ({
	(
		event: 'toggle',
		payload: {
			filterName: keyof FilterCollection;
			value: any;
			selectedState: 1 | 0;
			updateRouteState?: boolean;
			userSelectedPackages?: string[];
			trackEvent?: boolean;
		}
	): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { collectionType: CollectionType, currentFilters: FilterCollection, webLocale: WebLocale, addButtonVisible: boolean, showAddButtonFirst: boolean, userProviders: Package[], newPageType?: NewPageType, providersClickable: boolean, updateRouteState: boolean, trackEvent: boolean, userSelectedPackages?: string[], selectionType: 'single' | 'bundle' };

const FilterBarProviderMulti = () => import('@/components/filter-bar/seo/FilterBarProviderMulti.vue');





const IMG_PROVIDERS_BAR_LOAD_COUNT = globalThis.IMG_PROVIDERS_BAR_LOAD_COUNT ?? 8;
const plusIcon = `/${ASSETS_DIR}/img/plus_icon.png`;

const _providersById = ref<Record<number, Package>>({});
const isModalOpening = ref(false);
const providerUrls = ref<Record<string, string>>({});

const { subgenresList, allProvidersById, providersByShortName, setUpcomingProviders, genresByShortName } =
	useConstantStore();
const { toggle } = useFilterStore();
const { language, country } = useLanguageStore();
const { activeRoute } = useRoutingStore();
const { settings } = useUserStore();
const router = useRouter();

const enabled = computed(() =>
	['app.titles.upcoming', 'app.titles.new', 'app.titles.pricedrops'].some(routeName =>
		activeRoute.value?.name?.startsWith(routeName)
	)
);

const { result, onResult } = useQuery<GetUpcomingProvidersQuery, GetUpcomingProvidersQueryVariables>(
	GetUpcomingProvidersDocument,
	() => ({
		country: country.value,
		language: language.value,
		pageType: NewPageType.Upcoming,
		first: 32,
	}),
	{ enabled: enabled }
);

const upcomingProvidersData = computed(() => {
	const upcomingReleases =
		result.value?.newTitles?.edges
			?.map(title => {
				return title?.node?.content?.upcomingReleases;
			})
			?.flat() ?? [];

	const providerIds = upcomingReleases.map(provider => {
		return provider?.package?.packageId;
	});

	// Return array without duplicates or undefined values
	return [...new Set(providerIds)].filter(Boolean);
});

onResult(() => {
	setUpcomingProviders(upcomingProvidersData.value);
});

const selectedProviders = computed(() =>
	props.userProviders.filter(provider => props.currentFilters.providers.includes(provider.shortName))
);

const plusIconIsVisible = computed(() => {
	if (props.addButtonVisible) {
		return settings.value?.providers?.length > 0;
	}
	return props.addButtonVisible;
});

const filteredProviders = computed(() => {
	if (props.selectionType === 'bundle') {
		return props.userProviders.filter(provider => !props.currentFilters.providers.includes(provider.shortName));
	}
	return props.userProviders;
});

const recalculatedProviderUrlProp = computed(() => {
	return `${props.webLocale}_${isPricedropsPage.value ? 'pricedrop' : 'normal'}`;
});

const isPricedropsPage = computed(() => {
	return props.collectionType === CollectionType.NEW && activeRoute.value.meta?.newPageType === NewPageType.PriceDrop;
});

const providerTag = computed(() => (isPricedropsPage.value ? 'button' : 'a'));

watch(
	() => props.userProviders,
	() => {
		_providersById.value = allProvidersById.value;
	},
	{ immediate: true }
);

watch(
	recalculatedProviderUrlProp,
	async () => {
		const providersByShortNameVal = providersByShortName.value;
		const genresByShortNameVal = genresByShortName.value;
		const subgenresListVal = subgenresList.value;

		if (isPricedropsPage.value) {
			providerUrls.value = {};
		} else {
			providerUrls.value = props.userProviders.reduce((root, provider: Package) => {
				root[provider.shortName] = calculateUrl(
					router,
					undefined,
					props.collectionType,
					{
						// we only want content_types and providers in the <a href>.
						// no ?genres=crm or other filters.
						content_types: props.currentFilters.content_types,
						providers: [provider.shortName],
					},
					providersByShortNameVal,
					genresByShortNameVal,
					subgenresListVal
				);
				return root;
			}, {} as Record<string, string>);
		}
	},
	{ immediate: true }
);

const isFiltersContainsProvider = (providerName: string) => {
	return props.currentFilters.providers.includes(providerName);
};

const isProviderActive = (providerName: string) => {
	return props.currentFilters.providers.length === 0 || isFiltersContainsProvider(providerName);
};

const isProviderDisabled = (providerId: number) => {
	return props.collectionType === CollectionType.UPCOMING && !upcomingProvidersData.value.includes(providerId);
};

function providerClicked(providerName: string) {
	if (props.providersClickable) {
		const isActive: 1 | 0 = isFiltersContainsProvider(providerName) ? 0 : 1;
		const toggleData = {
			filterName: 'providers',
			value: providerName,
			selectedState: isActive,
			updateRouteState: props.updateRouteState,
			userSelectedPackages: props.userSelectedPackages,
			trackEvent: props.trackEvent,
		} as const;
		toggle(toggleData);
		emit('toggle', toggleData);
	}
}

async function openProviderSelection() {
	isModalOpening.value = true;
	await ModalHelper.openModal(
		PackagesSelectionModal,
		{ providerPlace: ProviderFilterPlace.SETTINGS },
		{ cssClass: 'packages-selection-modal' },
		ProviderFilterPlace.SETTINGS,
		true
	);
	isModalOpening.value = false;
}

return { __sfc: true,FilterBarProviderMulti, props, emit, IMG_PROVIDERS_BAR_LOAD_COUNT, plusIcon, _providersById, isModalOpening, providerUrls, subgenresList, allProvidersById, providersByShortName, setUpcomingProviders, genresByShortName, toggle, language, country, activeRoute, settings, router, enabled, result, onResult, upcomingProvidersData, selectedProviders, plusIconIsVisible, filteredProviders, recalculatedProviderUrlProp, isPricedropsPage, providerTag, isFiltersContainsProvider, isProviderActive, isProviderDisabled, providerClicked, openProviderSelection, HiddenHorizontalScrollbar, ProviderIcon }
}

})